import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, Save } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { DEFAULT_IMAGES, SLOTS, normalize, useSite } from "@/lib/site";
import { ImageSlot } from "./Images";
import { SectionsPanel } from "./SectionsPanel";
import { TextsPanel } from "./TextsPanel";

const PANELS = [["sections", "Sections"], ["photos", "Photos"], ["texts", "Text"]];

export const SiteEditor = () => {
  const { reload } = useSite();
  const [cfg, setCfg] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [panel, setPanel] = useState("sections");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/site").then((r) => setCfg(normalize(r.data?.config))).catch(() => setCfg(normalize(null)));
  }, []);
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (!cfg) return <p className="font-mono text-xs text-[var(--muted)] mt-6">LOADING…</p>;
  const change = (part) => { setCfg({ ...cfg, ...part }); setDirty(true); };
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/site", cfg);
      setDirty(false);
      reload();
      toast.success("Saved — the main page is updated");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="site-editor" className="mt-6">
      <div className="sticky top-0 z-20 -mx-1 px-1 py-3 bg-[var(--ink)]/95 backdrop-blur flex flex-wrap items-center justify-between gap-3 border-b border-[var(--line)] mb-6">
        <div className="flex border border-[var(--line)]">
          {PANELS.map(([v, l]) => (
            <button key={v} type="button" data-testid={`site-panel-${v}`} onClick={() => setPanel(v)}
              className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.15em] ${panel === v ? "bg-white text-black" : "hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <a href="/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-[var(--muted)] hover:text-white">
            <ExternalLink className="w-4 h-4" /> View site
          </a>
          <button type="button" data-testid="site-save-button" onClick={save} disabled={!dirty || busy}
            className="inline-flex items-center gap-2 clip-cut-sm bg-[var(--signal)] text-[var(--ink)] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[var(--bone)] disabled:opacity-40">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : dirty ? "Save changes" : "Saved"}
          </button>
        </div>
      </div>

      {panel === "sections" && <SectionsPanel sections={cfg.sections} onChange={(sections) => change({ sections })} />}
      {panel === "photos" && (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
          {SLOTS.map((slot) => (
            <div key={slot.key} className={slot.multi ? "sm:col-span-2 xl:col-span-3" : ""}>
              <ImageSlot slot={slot} value={cfg.images[slot.key]} fallback={DEFAULT_IMAGES[slot.key]}
                onChange={(list) => change({ images: { ...cfg.images, [slot.key]: list } })} />
            </div>
          ))}
        </div>
      )}
      {panel === "texts" && <TextsPanel texts={cfg.texts} onChange={(texts) => change({ texts })} />}
    </div>
  );
};
